import React, { useState, useMemo, useEffect } from "react";
import {
  Star,
  ChevronDown,
  ChevronRight,
  ArrowUpRight,
  MessageSquare,
  Filter,
  Check,
  Lock,
  X,
  MessageCircle,
  Flame,
  AlertCircle,
  HelpCircle,
  ThumbsUp,
  SlidersHorizontal,
} from "lucide-react";
import type { DataRecord, SocialQueueItem } from "../../types";
import { aspectoLabel, formatDateShort, stripGoogleTranslation } from "./socialFormat";
import { SocialBadge } from "./SocialBadge";
import { PLATFORM_LABELS, type SocialPlatformFilter } from "./SocialPlatformSelector";
import { SocialReplyModal } from "./SocialReplyModal";
import { getClientBrandMeta } from "./socialBrand";
import { SocialCommentMobileCard } from "./SocialCommentMobileCard";
import { SocialMobileFilterDrawer } from "./SocialMobileFilterDrawer";
import { SocialCommentDetailModal } from "./SocialCommentDetailModal";

const ACTO_DE_HABLA_LABELS: Record<string, string> = {
  reclamo: "Reclamo",
  pregunta: "Consulta",
  consulta: "Consulta",
  elogio: "Elogio",
  reaccion: "Reacción",
  ruido: "Ruido / Spam",
};

const MEDIA_TYPE_LABELS: Record<string, string> = {
  IMAGE: "Imagen",
  VIDEO: "Video",
  REEL: "Reel",
  CAROUSEL_ALBUM: "Carrusel",
  POST: "Post",
};

/**
 * Detecta si un comentario es exclusivamente emojis, reacciones breves o menciones vacías
 * (ruido visual que ahoga comentarios accionables con preguntas o quejas).
 */
export function isEmojiOnlyOrReaction(message: string, actoDeHabla?: string): boolean {
  const raw = (message || "").trim();
  if (!raw) return true;
  // Remover menciones @usuario y URLs
  const stripped = raw.replace(/@[\w._]+/g, "").replace(/https?:\/\/\S+/g, "").trim();
  if (!stripped) return true;
  // Si no contiene letras o números (Unicode), es solo emoji o símbolos/puntuación
  const hasSubstantiveText = /[\p{L}\p{N}]/u.test(stripped);
  if (!hasSubstantiveText) return true;
  // Si el acto de habla está clasificado como reacción y es muy corto (<= 3 chars, ej: "ok", "top")
  const acto = (actoDeHabla || "").toLowerCase().trim();
  if (acto === "reaccion" && stripped.length <= 3) return true;
  return false;
}

interface Props {
  comments: DataRecord[];
  clientSlug: string;
  clientName?: string;
  activePlatform: string;
  /** Ventana global del tab (YYYY-MM-DD); null = sin límite. */
  dateFrom: string | null;
  dateTo: string | null;
  replyEnabled?: boolean;
  clientReplyConfigured?: boolean;
  canReply?: boolean;
}

export function SocialCommentsTable({
  comments,
  clientSlug,
  clientName,
  activePlatform,
  dateFrom,
  dateTo,
  replyEnabled = false,
  clientReplyConfigured,
  canReply,
}: Props) {
  const isModuleConfigured = clientReplyConfigured !== undefined ? clientReplyConfigured : replyEnabled;
  const userCanReply = canReply !== undefined ? canReply : replyEnabled;
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());

  // Filtros
  const [filterContentMode, setFilterContentMode] = useState<"all" | "text_only" | "emoji_only">("all");
  const [filterSentiment, setFilterSentiment] = useState("ALL");
  const [filterUnansweredOnly, setFilterUnansweredOnly] = useState(false);
  const [filterOrigin, setFilterOrigin] = useState("ALL");
  const [filterStars, setFilterStars] = useState("ALL");
  const [filterActo, setFilterActo] = useState("ALL");
  const [filterAspecto, setFilterAspecto] = useState("ALL");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [selectedDetailRow, setSelectedDetailRow] = useState<DataRecord | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedReplyItem, setSelectedReplyItem] = useState<SocialQueueItem | null>(null);
  const [repliedIds, setRepliedIds] = useState<Set<string>>(new Set());
  const [localReplies, setLocalReplies] = useState<Record<string, DataRecord[]>>({});
  const PAGE_SIZE = 50;

  const text = (v: any, fallback = "") => (v !== null && v !== undefined ? String(v) : fallback);
  const number = (v: any, fallback = 0) => {
    const n = Number(v);
    return isNaN(n) ? fallback : n;
  };

  const shortDateLabel = formatDateShort;

  const displayMessage = (r: DataRecord) => {
    const raw = text(r.message);
    return text(r.platform).toLowerCase() === "gbp" ? stripGoogleTranslation(raw) : raw;
  };

  const actoOptions = useMemo(
    () => [...new Set(comments.map((r) => text(r.acto_de_habla, "")).filter(Boolean))].sort(),
    [comments],
  );

  const aspectoOptions = useMemo(
    () => [...new Set(comments.map((r) => text(r.aspecto, "")).filter(Boolean))].sort(),
    [comments],
  );

  const toggleThread = (rootId: string) => {
    setExpandedThreads((prev) => {
      const next = new Set(prev);
      if (next.has(rootId)) next.delete(rootId);
      else next.add(rootId);
      return next;
    });
  };

  // Agrupar por hilo (thread_root_id o feedback_id)
  const threads = useMemo(() => {
    const order: string[] = [];
    const byRoot = new Map<string, { root?: DataRecord; replies: DataRecord[] }>();
    for (const row of comments) {
      const rootId = text(row.thread_root_id) || text(row.feedback_id);
      if (!byRoot.has(rootId)) {
        byRoot.set(rootId, { replies: [] });
        order.push(rootId);
      }
      const entry = byRoot.get(rootId)!;
      if (row.is_reply) entry.replies.push(row);
      else entry.root = row;
    }

    // Integrar respuestas locales recién publicadas en la sesión
    for (const [rootId, addedReplies] of Object.entries(localReplies)) {
      if (byRoot.has(rootId)) {
        const entry = byRoot.get(rootId)!;
        for (const ar of addedReplies) {
          if (!entry.replies.some((existing) => text(existing.feedback_id) === text(ar.feedback_id))) {
            entry.replies.push(ar);
          }
        }
      }
    }

    return order.map((rootId) => byRoot.get(rootId)!).filter((t) => t.root);
  }, [comments, localReplies]);

  // Conteos vivos para las píldoras de la barra sticky según plataforma y fechas seleccionadas
  const platformAndDateThreads = useMemo(() => {
    return threads.filter(({ root }) => {
      if (!root) return false;
      if (activePlatform !== "all") {
        const plat = text(root.platform).toLowerCase();
        if (activePlatform === "meta_ig" && !(plat === "meta_ig" || plat.includes("ig") || plat.includes("instagram"))) return false;
        if (activePlatform === "meta_fb" && !(plat === "meta_fb" || plat.includes("fb") || plat.includes("facebook"))) return false;
        if (activePlatform === "gbp" && !(plat === "gbp" || plat.includes("google"))) return false;
      }
      const rowDate = text(root.date, "").slice(0, 10);
      if (dateFrom && rowDate < dateFrom) return false;
      if (dateTo && rowDate > dateTo) return false;
      return true;
    });
  }, [threads, activePlatform, dateFrom, dateTo]);

  const pillCounts = useMemo(() => {
    let withText = 0;
    let emojiOnly = 0;
    let reclamos = 0;
    let consultas = 0;
    let elogios = 0;
    let unanswered = 0;

    for (const { root, replies } of platformAndDateThreads) {
      if (!root) continue;
      const msg = displayMessage(root);
      const acto = text(root.acto_de_habla, "").toLowerCase();
      const isEmoji = isEmojiOnlyOrReaction(msg, acto);

      if (isEmoji) emojiOnly++;
      else withText++;

      if (acto === "reclamo") reclamos++;
      else if (acto === "consulta" || acto === "pregunta") consultas++;
      else if (acto === "elogio") elogios++;

      const hasOwnerReply =
        Boolean(root.has_reply) ||
        replies.some((rep) => Boolean(rep.is_owner_author)) ||
        repliedIds.has(text(root.feedback_id));
      if (!hasOwnerReply) unanswered++;
    }

    return {
      total: platformAndDateThreads.length,
      withText,
      emojiOnly,
      reclamos,
      consultas,
      elogios,
      unanswered,
    };
  }, [platformAndDateThreads, repliedIds]);

  // Filtrado de hilos por la raíz
  const filteredThreads = useMemo(() => {
    return threads.filter(({ root, replies }) => {
      if (!root) return false;

      // 1. Plataforma seleccionada a nivel superior
      if (activePlatform !== "all") {
        const plat = text(root.platform).toLowerCase();
        if (activePlatform === "meta_ig" && !(plat === "meta_ig" || plat.includes("ig") || plat.includes("instagram"))) return false;
        if (activePlatform === "meta_fb" && !(plat === "meta_fb" || plat.includes("fb") || plat.includes("facebook"))) return false;
        if (activePlatform === "gbp" && !(plat === "gbp" || plat.includes("google"))) return false;
      }

      // 2. Ventana global del tab Social (selector del header)
      const rowDate = text(root.date, "").slice(0, 10);
      if (dateFrom && rowDate < dateFrom) return false;
      if (dateTo && rowDate > dateTo) return false;

      // 3. Modo de contenido: Con texto vs Solo emojis
      if (filterContentMode !== "all") {
        const msg = displayMessage(root);
        const isEmoji = isEmojiOnlyOrReaction(msg, text(root.acto_de_habla));
        if (filterContentMode === "text_only" && isEmoji) return false;
        if (filterContentMode === "emoji_only" && !isEmoji) return false;
      }

      // 4. Filtro Sentimiento (polaridad)
      if (filterSentiment !== "ALL") {
        const pol = text(root.polaridad, "").toLowerCase();
        if (filterSentiment === "sin_clasificar") {
          if (pol !== "" && pol !== "sin clasificar") return false;
        } else if (pol !== filterSentiment.toLowerCase()) {
          return false;
        }
      }

      // 5. Solo sin responder
      if (filterUnansweredOnly) {
        const hasOwnerReply =
          Boolean(root.has_reply) ||
          replies.some((rep) => Boolean(rep.is_owner_author)) ||
          repliedIds.has(text(root.feedback_id));
        if (hasOwnerReply) return false;
      }

      // 6. Origen (anuncios / orgánico)
      if (filterOrigin !== "ALL") {
        const orig = text(root.origin, "").toLowerCase();
        if (orig !== filterOrigin.toLowerCase()) return false;
      }

      // 7. Calificación de estrellas
      if (filterStars !== "ALL") {
        const rating =
          root.star_rating !== null && root.star_rating !== undefined ? Math.round(number(root.star_rating)) : null;
        if (rating === null || String(rating) !== filterStars) return false;
      }

      // 8. Tipo (acto de habla) - normaliza consulta / pregunta
      if (filterActo !== "ALL") {
        const rowActo = text(root.acto_de_habla).toLowerCase();
        if (filterActo.toLowerCase() === "consulta") {
          if (rowActo !== "consulta" && rowActo !== "pregunta") return false;
        } else if (rowActo !== filterActo.toLowerCase()) {
          return false;
        }
      }

      // 9. Aspecto
      if (filterAspecto !== "ALL" && text(root.aspecto) !== filterAspecto) return false;

      return true;
    });
  }, [
    threads,
    activePlatform,
    dateFrom,
    dateTo,
    filterContentMode,
    filterSentiment,
    filterUnansweredOnly,
    filterOrigin,
    filterStars,
    filterActo,
    filterAspecto,
    repliedIds,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    activePlatform,
    dateFrom,
    dateTo,
    filterContentMode,
    filterSentiment,
    filterUnansweredOnly,
    filterOrigin,
    filterStars,
    filterActo,
    filterAspecto,
  ]);

  const clearFilters = () => {
    setFilterContentMode("all");
    setFilterSentiment("ALL");
    setFilterUnansweredOnly(false);
    setFilterOrigin("ALL");
    setFilterStars("ALL");
    setFilterActo("ALL");
    setFilterAspecto("ALL");
    setCurrentPage(1);
  };

  const filtersActive = Boolean(
    filterContentMode !== "all" ||
      filterSentiment !== "ALL" ||
      filterUnansweredOnly ||
      filterOrigin !== "ALL" ||
      filterStars !== "ALL" ||
      filterActo !== "ALL" ||
      filterAspecto !== "ALL"
  );

  const totalThreads = filteredThreads.length;
  const totalPages = Math.max(1, Math.ceil(totalThreads / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, currentPage), totalPages);

  const pagedThreads = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredThreads.slice(start, start + PAGE_SIZE);
  }, [filteredThreads, safePage]);

  const visibleRows = useMemo(() => {
    const rows: (DataRecord & { _isReplyRow?: boolean; _replyCount?: number; _hasOwnerReply?: boolean })[] = [];
    for (const { root, replies } of pagedThreads) {
      const hasOwnerReply = Boolean(root!.has_reply) || replies.some((rep) => Boolean(rep.is_owner_author));
      const isReplied = hasOwnerReply || repliedIds.has(text(root!.feedback_id));
      rows.push({
        ...root!,
        has_reply: isReplied,
        _replyCount: replies.length,
        _hasOwnerReply: hasOwnerReply,
      });
      if (expandedThreads.has(text(root!.thread_root_id) || text(root!.feedback_id))) {
        for (const reply of replies) {
          rows.push({ ...reply, _isReplyRow: true });
        }
      }
    }
    return rows;
  }, [pagedThreads, expandedThreads]);

  const threadRepliesMap = useMemo(() => {
    const map: Record<string, DataRecord[]> = {};
    for (const { root, replies } of pagedThreads) {
      if (root) {
        const rootId = text(root.thread_root_id) || text(root.feedback_id);
        map[rootId] = replies;
      }
    }
    return map;
  }, [pagedThreads]);

  function isCdnMediaUrl(url: string): boolean {
    if (!url) return false;
    const lower = url.toLowerCase();
    return (
      lower.includes("scontent") ||
      lower.includes("cdninstagram.com") ||
      lower.includes("fbcdn.net") ||
      lower.endsWith(".jpg") ||
      lower.endsWith(".jpeg") ||
      lower.endsWith(".png") ||
      lower.endsWith(".webp") ||
      lower.endsWith(".mp4") ||
      lower.includes(".jpg?") ||
      lower.includes(".jpeg?") ||
      lower.includes(".png?") ||
      lower.includes(".webp?") ||
      lower.includes(".mp4?")
    );
  }

  function buildActionLink(row: DataRecord): { label: string; href: string } | null {
    const origin = text(row.origin).toLowerCase();
    const platform = text(row.platform).toLowerCase();

    // 1. Google Business Profile
    if (platform === "gbp" || platform.includes("google")) {
      const directUrl = text(row.post_media_url) || text(row.post_permalink);
      if (directUrl && directUrl.startsWith("https://maps.google.com")) {
        return {
          label: "Ver en Google Maps",
          href: directUrl,
        };
      }
      const brandMeta = getClientBrandMeta(clientSlug);
      const displayName = text(row.client_name) || clientName || brandMeta.displayName;
      const postTitle = text(row.post_title);
      const hasSpecificTitle = postTitle && postTitle !== "—" && !postTitle.startsWith("SIN_") && postTitle !== displayName;
      const searchQuery = hasSpecificTitle
        ? `${displayName} ${postTitle}`
        : `${displayName} opiniones`;
      return {
        label: "Ver en Google Maps",
        href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(searchQuery)}`,
      };
    }

    // 2. Instagram
    if (platform === "meta_ig" || platform.includes("ig") || platform.includes("instagram")) {
      const permalink = text(row.post_permalink);
      if (permalink && permalink.startsWith("http") && !isCdnMediaUrl(permalink) && permalink.includes("instagram.com")) {
        return {
          label: "Ver en Instagram",
          href: permalink,
        };
      }
      const brandMeta = getClientBrandMeta(clientSlug);
      const handle = brandMeta.instagramHandle || clientSlug;
      return {
        label: "Ver en Instagram",
        href: `https://www.instagram.com/${handle}/`,
      };
    }

    // 3. Facebook
    if (platform === "meta_fb" || platform.includes("fb") || platform.includes("facebook")) {
      const permalink = text(row.post_permalink);
      if (permalink && permalink.startsWith("http") && !isCdnMediaUrl(permalink) && permalink.includes("facebook.com")) {
        return {
          label: "Ver en Facebook",
          href: permalink,
        };
      }
      const postId = text(row.post_id);
      const [pageId, postSuffix] = postId.split("_");
      if (pageId && postSuffix) {
        return {
          label: "Ver en Facebook",
          href: `https://www.facebook.com/${pageId}/posts/${postSuffix}`,
        };
      }
      return {
        label: "Ver en Facebook",
        href: "https://www.facebook.com/",
      };
    }
    return null;
  }

  const getSentimentBadge = (pol: string) => {
    const p = pol.toLowerCase().trim();
    if (p === "negativa" || p === "negativo") {
      return <SocialBadge tone="danger">Negativo</SocialBadge>;
    }
    if (p === "positiva" || p === "positivo") {
      return <SocialBadge tone="success">Positivo</SocialBadge>;
    }
    if (p === "neutra" || p === "neutro") {
      return <SocialBadge tone="neutral">Neutro</SocialBadge>;
    }
    return <span style={{ color: "var(--dim, #5e6677)", fontSize: "11px" }}>Sin clasificar</span>;
  };

  const showStarsFilter =
    activePlatform === "gbp" ||
    (activePlatform === "all" && comments.some((c) => c.star_rating !== null && c.star_rating !== undefined));

  const isMetaIg = activePlatform === "meta_ig";
  const isMetaFb = activePlatform === "meta_fb";
  const isGoogle = activePlatform === "gbp";

  const panelTitle = isGoogle
    ? "Reseñas de Google BP"
    : isMetaIg
    ? "Comentarios de Instagram"
    : isMetaFb
    ? "Comentarios de Facebook"
    : "Comentarios y Reseñas";

  const platformBadgeText = isMetaIg
    ? "Instagram"
    : isMetaFb
    ? "Facebook"
    : isGoogle
    ? "Google BP"
    : "Visión Global";

  const platformBadgeColor = isMetaIg
    ? "#e1306c"
    : isMetaFb
    ? "#1877f2"
    : isGoogle
    ? "#34a853"
    : "#7d96ff";

  return (
    <div
      id="section-social-comments-table"
      className="social-comments-panel"
      style={{
        background: "var(--surface, #11141b)",
        border: "1px solid var(--line, rgba(255, 255, 255, 0.08))",
        borderRadius: "14px",
        padding: "20px 24px",
        marginBottom: "24px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
          marginBottom: "16px",
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "var(--text, #f6f7fb)" }}>
            {panelTitle}
          </h3>
          <p style={{ margin: "4px 0 0 0", fontSize: "12px", color: "var(--muted, #8e96a8)" }}>
            Hilos de la ventana elegida en el header, con estado de respuesta y filtros.
          </p>
        </div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
          {activePlatform !== "all" && (
            <span
              className="badge"
              style={{
                fontSize: "12px",
                background: isMetaIg
                  ? "rgba(225, 48, 108, 0.15)"
                  : isMetaFb
                  ? "rgba(24, 119, 242, 0.15)"
                  : "rgba(66, 133, 244, 0.15)",
                color: platformBadgeColor,
                fontWeight: 700,
                borderColor: "transparent",
              }}
            >
              {platformBadgeText}
            </span>
          )}
          <span className="badge" style={{ fontSize: "12px" }}>
            {totalThreads} {totalThreads === 1 ? "hilo filtrado" : "hilos filtrados"}
          </span>
        </div>
      </div>

      {/* Barra de Filtros Rápidos Mobile (solo visible en <= 768px) */}
      <div className="social-mobile-filter-trigger-bar">
        <button
          type="button"
          className="social-mobile-filter-trigger-btn"
          onClick={() => setIsMobileFilterOpen(true)}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Filter size={15} color="var(--blue, #4285f4)" />
            <span>Filtros y opciones</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            {filtersActive && (
              <span className="badge" style={{ background: "#4285f4", color: "#fff", fontSize: "11px" }}>
                Activos
              </span>
            )}
            <span style={{ fontSize: "12px", color: "var(--muted, #8e96a8)" }}>
              {totalThreads} {totalThreads === 1 ? "hilo" : "hilos"}
            </span>
          </div>
        </button>
      </div>

      {/* Barra Sticky de Filtros y Contexto Activo */}
      <div className="social-comments-sticky-bar">
        {/* Fila 1: Píldoras Rápidas (Segmentador de contenido + Tipos prioritarios + Sin responder) */}
        <div className="social-pills-row">
          {/* Segmentador de contenido */}
          <button
            type="button"
            className={`social-filter-pill ${filterContentMode === "all" ? "is-active" : ""}`}
            onClick={() => setFilterContentMode("all")}
            title="Mostrar todos los comentarios e hilos"
          >
            <MessageSquare size={13} />
            <span>Todos</span>
            <span className="pill-badge">{pillCounts.total}</span>
          </button>

          <button
            type="button"
            className={`social-filter-pill ${filterContentMode === "text_only" ? "is-active" : ""}`}
            onClick={() => setFilterContentMode(filterContentMode === "text_only" ? "all" : "text_only")}
            title="Ocultar comentarios que solo tienen emojis o menciones vacías"
          >
            <MessageCircle size={13} />
            <span>Con texto</span>
            <span className="pill-badge">{pillCounts.withText}</span>
          </button>

          <button
            type="button"
            className={`social-filter-pill ${filterContentMode === "emoji_only" ? "is-active" : ""}`}
            onClick={() => setFilterContentMode(filterContentMode === "emoji_only" ? "all" : "emoji_only")}
            title="Mostrar únicamente comentarios de solo emojis / reacciones breves"
          >
            <Flame size={13} />
            <span>Solo emojis</span>
            <span className="pill-badge">{pillCounts.emojiOnly}</span>
          </button>

          <div className="social-pills-divider" />

          {/* Píldoras de tipo / acto de habla */}
          <button
            type="button"
            className={`social-filter-pill is-danger ${filterActo === "reclamo" ? "is-active" : ""}`}
            onClick={() => setFilterActo(filterActo === "reclamo" ? "ALL" : "reclamo")}
            title="Filtrar quejas y reclamos"
          >
            <AlertCircle size={13} />
            <span>Reclamos</span>
            <span className="pill-badge">{pillCounts.reclamos}</span>
          </button>

          <button
            type="button"
            className={`social-filter-pill is-warning ${filterActo === "consulta" ? "is-active" : ""}`}
            onClick={() => setFilterActo(filterActo === "consulta" ? "ALL" : "consulta")}
            title="Filtrar consultas y preguntas de usuarios"
          >
            <HelpCircle size={13} />
            <span>Consultas</span>
            <span className="pill-badge">{pillCounts.consultas}</span>
          </button>

          <button
            type="button"
            className={`social-filter-pill is-success ${filterActo === "elogio" ? "is-active" : ""}`}
            onClick={() => setFilterActo(filterActo === "elogio" ? "ALL" : "elogio")}
            title="Filtrar felicitaciones y elogios"
          >
            <ThumbsUp size={13} />
            <span>Elogios</span>
            <span className="pill-badge">{pillCounts.elogios}</span>
          </button>

          <div className="social-pills-divider" />

          {/* Sin responder */}
          <button
            type="button"
            className={`social-filter-pill is-warning ${filterUnansweredOnly ? "is-active" : ""}`}
            onClick={() => setFilterUnansweredOnly(!filterUnansweredOnly)}
            title="Filtrar comentarios que aún no tienen respuesta oficial"
          >
            <span>⏳ Sin responder</span>
            <span className="pill-badge">{pillCounts.unanswered}</span>
          </button>

          <div style={{ flex: 1 }} />

          {/* Botón toggle de filtros secundarios */}
          <button
            type="button"
            className={`social-advanced-toggle-btn ${showAdvancedFilters ? "is-open" : ""}`}
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            title="Filtros avanzados (Sentimiento, Origen, Estrellas, Aspecto)"
          >
            <SlidersHorizontal size={13} />
            <span>Más filtros</span>
            <ChevronDown
              size={13}
              style={{
                transform: showAdvancedFilters ? "rotate(180deg)" : "none",
                transition: "transform 0.15s ease",
              }}
            />
          </button>
        </div>

        {/* Fila 2: Chips de Contexto Activo (siempre visibles en sticky al scrollear hacia abajo) */}
        {filtersActive && (
          <div className="social-active-chips-bar">
            <span style={{ color: "var(--muted, #8e96a8)", fontWeight: 650 }}>Filtros activos:</span>

            {filterContentMode === "text_only" && (
              <span className="social-active-chip">
                💬 Con texto
                <button type="button" onClick={() => setFilterContentMode("all")} aria-label="Quitar filtro con texto">
                  <X size={11} />
                </button>
              </span>
            )}

            {filterContentMode === "emoji_only" && (
              <span className="social-active-chip">
                🔥 Solo emojis
                <button type="button" onClick={() => setFilterContentMode("all")} aria-label="Quitar filtro solo emojis">
                  <X size={11} />
                </button>
              </span>
            )}

            {filterActo !== "ALL" && (
              <span className="social-active-chip">
                Tipo: {ACTO_DE_HABLA_LABELS[filterActo] ?? filterActo}
                <button type="button" onClick={() => setFilterActo("ALL")} aria-label="Quitar filtro de tipo">
                  <X size={11} />
                </button>
              </span>
            )}

            {filterUnansweredOnly && (
              <span className="social-active-chip">
                ⏳ Sin responder
                <button type="button" onClick={() => setFilterUnansweredOnly(false)} aria-label="Quitar filtro sin responder">
                  <X size={11} />
                </button>
              </span>
            )}

            {filterSentiment !== "ALL" && (
              <span className="social-active-chip">
                Sentimiento: {filterSentiment}
                <button type="button" onClick={() => setFilterSentiment("ALL")} aria-label="Quitar filtro sentimiento">
                  <X size={11} />
                </button>
              </span>
            )}

            {filterOrigin !== "ALL" && (
              <span className="social-active-chip">
                Origen: {filterOrigin === "paid" ? "Anuncios (Paid)" : "Orgánico"}
                <button type="button" onClick={() => setFilterOrigin("ALL")} aria-label="Quitar filtro origen">
                  <X size={11} />
                </button>
              </span>
            )}

            {filterStars !== "ALL" && (
              <span className="social-active-chip">
                ★ {filterStars} {filterStars === "1" ? "estrella" : "estrellas"}
                <button type="button" onClick={() => setFilterStars("ALL")} aria-label="Quitar filtro estrellas">
                  <X size={11} />
                </button>
              </span>
            )}

            {filterAspecto !== "ALL" && (
              <span className="social-active-chip">
                Aspecto: {aspectoLabel(filterAspecto)}
                <button type="button" onClick={() => setFilterAspecto("ALL")} aria-label="Quitar filtro aspecto">
                  <X size={11} />
                </button>
              </span>
            )}

            <div style={{ flex: 1 }} />

            <button type="button" className="social-clear-all-btn" onClick={clearFilters}>
              <X size={12} />
              Limpiar todo
            </button>

            <span style={{ color: "var(--dim, #6d7587)", fontSize: "11px", marginLeft: "6px" }}>
              Mostrando {totalThreads} de {pillCounts.total}
            </span>
          </div>
        )}

        {/* Fila 3: Filtros secundarios colapsables */}
        {showAdvancedFilters && (
          <div className="social-advanced-filters-panel">
            {/* Sentimiento */}
            <label className="account-filter">
              Sentimiento
              <select value={filterSentiment} onChange={(e) => setFilterSentiment(e.target.value)}>
                <option value="ALL">Todos los sentimientos</option>
                <option value="negativa">Negativo</option>
                <option value="positiva">Positivo</option>
                <option value="neutra">Neutro</option>
                <option value="sin_clasificar">Sin clasificar</option>
              </select>
            </label>

            {/* Origen */}
            <label className="account-filter">
              Origen
              <select value={filterOrigin} onChange={(e) => setFilterOrigin(e.target.value)}>
                <option value="ALL">Todos (Paid y Orgánico)</option>
                <option value="organic">Orgánico</option>
                <option value="paid">Anuncios (Paid)</option>
              </select>
            </label>

            {/* Calificación (solo para plataformas con reseñas de estrellas) */}
            {showStarsFilter && (
              <label className="account-filter">
                Calificación
                <select value={filterStars} onChange={(e) => setFilterStars(e.target.value)}>
                  <option value="ALL">Todas las estrellas</option>
                  <option value="5">5 estrellas ★★★★★</option>
                  <option value="4">4 estrellas ★★★★</option>
                  <option value="3">3 estrellas ★★★</option>
                  <option value="2">2 estrellas ★★</option>
                  <option value="1">1 estrella ★</option>
                </select>
              </label>
            )}

            {/* Aspecto */}
            {aspectoOptions.length > 0 && (
              <label className="account-filter">
                Aspecto
                <select value={filterAspecto} onChange={(e) => setFilterAspecto(e.target.value)}>
                  <option value="ALL">Todos los aspectos</option>
                  {aspectoOptions.map((v) => (
                    <option key={v} value={v}>
                      {aspectoLabel(v)}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}
      </div>

      {/* Tabla de Resultados (Vista Desktop) */}
      <div className="social-desktop-table-container">
        <div className="table-responsive" style={{ overflowX: "auto" }}>
        <table className="compact-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--line, rgba(255, 255, 255, 0.1))" }}>
              <th style={{ textAlign: "left", padding: "10px 12px", color: "var(--muted, #8e96a8)", fontWeight: 650 }}>
                Fecha
              </th>
              <th style={{ textAlign: "left", padding: "10px 12px", color: "var(--muted, #8e96a8)", fontWeight: 650 }}>
                Red / Canal
              </th>
              <th style={{ textAlign: "left", padding: "10px 12px", color: "var(--muted, #8e96a8)", fontWeight: 650 }}>
                Autor
              </th>
              <th style={{ textAlign: "left", padding: "10px 12px", color: "var(--muted, #8e96a8)", fontWeight: 650 }}>
                Mensaje
              </th>
              <th style={{ textAlign: "left", padding: "10px 12px", color: "var(--muted, #8e96a8)", fontWeight: 650 }}>
                Sentimiento
              </th>
              <th style={{ textAlign: "left", padding: "10px 12px", color: "var(--muted, #8e96a8)", fontWeight: 650 }}>
                Tipo / Aspecto
              </th>
              <th style={{ textAlign: "left", padding: "10px 12px", color: "var(--muted, #8e96a8)", fontWeight: 650 }}>
                Acción
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: "32px", color: "var(--dim, #5e6677)" }}>
                  No se encontraron comentarios que coincidan con los filtros seleccionados.
                </td>
              </tr>
            ) : (
              visibleRows.map((r, idx) => {
                const rootId = text(r.thread_root_id) || text(r.feedback_id);
                const count = number(r._replyCount);
                const isOpen = expandedThreads.has(rootId);
                const rating = r.star_rating !== null && r.star_rating !== undefined ? number(r.star_rating) : null;
                const link = buildActionLink(r);
                const isReply = Boolean(r._isReplyRow);
                const isOwner = Boolean(r.is_owner_author);
                const plat = text(r.platform).toLowerCase();
                const isPaid = text(r.origin).toLowerCase() === "paid";
                const isReplied = Boolean(r.has_reply) || Boolean(r._hasOwnerReply) || repliedIds.has(text(r.feedback_id));

                return (
                  <tr
                    key={String(r.feedback_id || idx)}
                    style={{
                      borderBottom: "1px solid var(--line, rgba(255, 255, 255, 0.05))",
                      background: isReply
                        ? "var(--surface-2, rgba(255, 255, 255, 0.04))"
                        : isOwner
                        ? "rgba(125, 150, 255, 0.04)"
                        : "transparent",
                    }}
                  >
                    {/* Fecha */}
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap", color: "var(--muted, #8e96a8)" }}>
                      {shortDateLabel(text(r.created_time || r.date, "—"))}
                    </td>

                    {/* Red / Canal */}
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                      {!isReply && (
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <SocialBadge tone="info">
                            {plat === "gbp" ? "Google BP" : plat.includes("fb") ? "Facebook" : "Instagram"}
                          </SocialBadge>
                          {plat !== "gbp" && <SocialBadge tone="neutral">{isPaid ? "Anuncios" : "Orgánico"}</SocialBadge>}
                        </div>
                      )}
                    </td>

                    {/* Autor */}
                    <td style={{ padding: "10px 12px", maxWidth: "140px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <span
                        style={{
                          fontWeight: isOwner ? 700 : 500,
                          color: isOwner ? "var(--blue, #7d96ff)" : "var(--text, #10131a)",
                        }}
                        title={text(r.author_name)}
                      >
                        {isOwner ? "👑 Cuenta oficial" : text(r.author_name, "Anónimo")}
                      </span>
                    </td>

                    {/* Mensaje */}
                    <td style={{ padding: "10px 12px", maxWidth: "420px", wordBreak: "break-word" }}>
                      {isReply ? (
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", paddingLeft: "16px" }}>
                          <span style={{ color: "var(--blue, #7d96ff)", fontWeight: 700 }}>↳</span>
                          <div>
                            <div style={{ color: "var(--text, #10131a)", fontSize: "12px", lineHeight: "1.4" }}>
                              {text(r.message)}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div>
                          {rating !== null && rating > 0 && (
                            <div style={{ display: "flex", alignItems: "center", gap: "3px", marginBottom: "4px" }}>
                              {[1, 2, 3, 4, 5].map((s) => (
                                <Star
                                  key={s}
                                  size={12}
                                  fill={s <= rating ? "#f59e0b" : "none"}
                                  stroke={s <= rating ? "#f59e0b" : "#64748b"}
                                />
                              ))}
                              <span style={{ fontSize: "11px", fontWeight: 600, color: "#f59e0b", marginLeft: "4px" }}>
                                {rating} de 5
                              </span>
                            </div>
                          )}
                          <div style={{ color: displayMessage(r).trim() ? "var(--text, #10131a)" : "var(--muted, #8e96a8)", fontStyle: displayMessage(r).trim() ? "normal" : "italic", lineHeight: "1.4" }}>
                            {displayMessage(r).trim() ? displayMessage(r) : (rating ? "Sin texto (solo estrellas)" : "—")}
                          </div>
                          {count > 0 && (
                            <button
                              type="button"
                              onClick={() => toggleThread(rootId)}
                              style={{
                                marginTop: "6px",
                                background: "transparent",
                                border: "none",
                                color: "var(--blue, #7d96ff)",
                                fontSize: "11px",
                                fontWeight: 650,
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                padding: 0,
                              }}
                            >
                              {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                              {isOpen ? "Ocultar respuestas" : `Ver ${count} ${count === 1 ? "respuesta" : "respuestas"}`}
                            </button>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Sentimiento */}
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                      {!isReply && getSentimentBadge(text(r.polaridad))}
                    </td>

                    {/* Tipo / Aspecto */}
                    <td style={{ padding: "10px 12px", fontSize: "12px", whiteSpace: "nowrap" }}>
                      {!isReply && (
                        <div>
                          <div style={{ fontWeight: 600, color: "var(--text, #10131a)" }}>
                            {ACTO_DE_HABLA_LABELS[text(r.acto_de_habla)] ?? text(r.acto_de_habla, "—")}
                          </div>
                          {text(r.aspecto) && (
                            <div style={{ color: "var(--muted, #8e96a8)", fontSize: "11px" }}>{aspectoLabel(text(r.aspecto))}</div>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Acción */}
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                      {!isReply && (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          {link && (
                            <a
                              href={link.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "3px",
                                color: "var(--blue, #7d96ff)",
                                fontSize: "12px",
                                fontWeight: 600,
                              }}
                            >
                              {link.label}
                              <ArrowUpRight size={12} />
                            </a>
                          )}
                          {isReplied ? (
                            <button
                              type="button"
                              className="button button-ghost button-sm"
                              disabled
                              style={{
                                fontSize: "11px",
                                padding: "2px 8px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                opacity: 0.65,
                                cursor: "not-allowed",
                                color: "var(--muted, #8e96a8)",
                                border: "1px solid var(--line, rgba(255, 255, 255, 0.12))",
                                background: "rgba(255, 255, 255, 0.03)",
                              }}
                              title="Este comentario ya cuenta con una respuesta registrada"
                            >
                              <Check size={12} />
                              Respondido
                            </button>
                          ) : !userCanReply || !isModuleConfigured ? (
                            <button
                              type="button"
                              className="button button-ghost button-sm"
                              disabled
                              style={{
                                fontSize: "11px",
                                padding: "2px 8px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                opacity: 0.5,
                                cursor: "not-allowed",
                                color: "var(--muted, #8e96a8)",
                                border: "1px solid var(--line, rgba(255, 255, 255, 0.12))",
                                background: "rgba(255, 255, 255, 0.02)",
                              }}
                              title={!isModuleConfigured ? "Módulo de respuesta no habilitado para este cliente" : "Usuario no autorizado para responder"}
                            >
                              <Lock size={12} />
                              Responder
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="button button-ghost button-sm"
                              style={{
                                fontSize: "11px",
                                padding: "2px 8px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                color: "var(--blue, #7d96ff)",
                                border: "1px solid rgba(125, 150, 255, 0.3)",
                                background: "rgba(125, 150, 255, 0.06)",
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                const queueItem: SocialQueueItem = {
                                  feedback_id: text(r.feedback_id),
                                  date: text(r.date),
                                  created_time: text(r.created_time || r.date),
                                  platform: text(r.platform),
                                  origin: text(r.origin, "organic"),
                                  author_name: text(r.author),
                                  message: text(r.message),
                                  star_rating: r.star_rating !== undefined && r.star_rating !== null ? Number(r.star_rating) : null,
                                  has_reply: Boolean(r.has_reply),
                                  post_id: text(r.post_id) || null,
                                  ad_account_id: text(r.ad_account_id) || null,
                                  page_id: text(r.page_id) || null,
                                  action_link: link,
                                  polaridad: text(r.polaridad) || null,
                                  acto_de_habla: text(r.acto_de_habla) || null,
                                  aspecto: text(r.aspecto) || null,
                                  plan: {
                                    action: "reply",
                                    priority: 2,
                                    reason: null,
                                    template_key: null,
                                    draft_text: null,
                                  },
                                };
                                setSelectedReplyItem(queueItem);
                              }}
                            >
                              <MessageSquare size={12} />
                              Responder
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Feed Mobile de Cards Nativas (solo visible en <= 768px) */}
      <div className="social-mobile-card-feed">
        {visibleRows.length === 0 ? (
          <div
            style={{
              padding: "32px 16px",
              textAlign: "center",
              background: "var(--surface, #141822)",
              borderRadius: "12px",
              color: "var(--dim, #6d7587)",
              fontSize: "13px",
            }}
          >
            No se encontraron comentarios que coincidan con los filtros seleccionados.
          </div>
        ) : (
          visibleRows
            .filter((r) => !r._isReplyRow)
            .map((row) => {
              const rootId = text(row.thread_root_id) || text(row.feedback_id);
              const replies = (threadRepliesMap[rootId] ?? []).concat(localReplies[rootId] ?? []);
            return (
              <SocialCommentMobileCard
                key={text(row.feedback_id) || text(row.date)}
                row={row}
                replies={replies}
                clientSlug={clientSlug}
                userCanReply={userCanReply}
                isModuleConfigured={isModuleConfigured}
                onOpenReply={(item) => setSelectedReplyItem(item)}
                onOpenDetailModal={(r) => setSelectedDetailRow(r)}
                buildActionLink={(r) => buildActionLink(r)}
              />
            );
          })
        )}
      </div>

      {/* Barra de Paginación */}
      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "16px",
            paddingTop: "12px",
            borderTop: "1px solid var(--line, rgba(255, 255, 255, 0.06))",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <span style={{ fontSize: "12px", color: "var(--muted, #8e96a8)" }}>
            Mostrando hilos <strong>{(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, totalThreads)}</strong> de{" "}
            <strong>{totalThreads}</strong>
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <button
              type="button"
              className="button button-ghost button-sm"
              disabled={safePage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              ‹ Anterior
            </button>
            <span style={{ fontSize: "12px", color: "var(--muted, #8e96a8)" }}>
              Página {safePage} de {totalPages}
            </span>
            <button
              type="button"
              className="button button-ghost button-sm"
              disabled={safePage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              Siguiente ›
            </button>
          </div>
        </div>
      )}

      {/* Modal de respuesta asistida HITL */}
      <SocialReplyModal
        isOpen={Boolean(selectedReplyItem)}
        onClose={() => setSelectedReplyItem(null)}
        item={selectedReplyItem}
        clientSlug={clientSlug}
        onReplySuccess={(feedbackId, _method, approvedText) => {
          setRepliedIds((prev) => new Set(prev).add(feedbackId));
          if (approvedText) {
            const nowIso = new Date().toISOString();
            const newReplyRecord: DataRecord = {
              feedback_id: `${feedbackId}_local_reply`,
              parent_feedback_id: feedbackId,
              thread_root_id: feedbackId,
              is_reply: true,
              is_owner_author: true,
              author_name: clientSlug === "wolf" ? "Wolf Digital" : "Cuenta oficial",
              message: approvedText,
              date: nowIso.slice(0, 10),
              created_time: nowIso,
            };
            setLocalReplies((prev) => ({
              ...prev,
              [feedbackId]: [...(prev[feedbackId] || []), newReplyRecord],
            }));
            setExpandedThreads((prev) => new Set(prev).add(feedbackId));
          }
        }}
      />

      {/* Drawer de Filtros Mobile */}
      <SocialMobileFilterDrawer
        isOpen={isMobileFilterOpen}
        onClose={() => setIsMobileFilterOpen(false)}
        activePlatform={activePlatform}
        filterContentMode={filterContentMode}
        onChangeContentMode={setFilterContentMode}
        filterSentiment={filterSentiment}
        onChangeSentiment={setFilterSentiment}
        filterUnansweredOnly={filterUnansweredOnly}
        onToggleUnansweredOnly={setFilterUnansweredOnly}
        filterStars={filterStars}
        onChangeStars={setFilterStars}
        onClearAll={clearFilters}
        totalFiltered={totalThreads}
      />

      {/* Modal para ver comentario completo si el usuario pulsa en mobile o desktop */}
      <SocialCommentDetailModal
        isOpen={Boolean(selectedDetailRow)}
        onClose={() => setSelectedDetailRow(null)}
        item={selectedDetailRow}
        clientSlug={clientSlug}
        onOpenReplyModal={(item) => setSelectedReplyItem(item)}
        getDirectLink={(r) => buildActionLink(r)?.href || "#"}
      />
    </div>
  );
}
